import React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSettings } from '../Settings';
import { useBrand } from '../Brand';
import { useTranslation } from '../i18n';
import { LayoutDefault } from './default/LayoutDefault';
import { LayoutLegacy } from './legacy/LayoutLegacy';
import { NotFound } from '../components/common/NotFound';
import { Healthcheck } from '../components/common/Healthcheck';
import { ROUTES } from '../utils/routes';
import type { PageConfig } from '../types/settings';

/**
 * Tabela de rotas.
 *
 * Cada página declarada em `mainpage.json` é registrada UMA única vez, no seu
 * caminho canônico (`/`, `/planos-lista`, `/cartao-credito`,
 * `/renova_plano`): a marca vem do hostname, então não existe par prefixado
 * `/{marca}/…` nem primeiro segmento a interpretar.
 *
 * **Duas rotas ficam FORA do gate da marca**, de propósito:
 *
 * - `/renova_plano` — Fluxo A, contrato externo do Link de Venda Direta. Ela
 *   identifica a operadora pelo próprio registro da recarga (`t` + `id`), não
 *   pela marca do hostname. Enquanto a marca vinha da URL, essa página escapava
 *   do gate por acidente (o caminho não tinha slug); agora que toda origem tem
 *   marca, a exclusão precisa ser explícita — senão uma marca suspensa no ERP,
 *   ou uma falha ao buscar a identidade, derrubaria uma página que é contrato
 *   com outro produto.
 * - `/healthcheck` — sonda de infraestrutura: o container está saudável mesmo
 *   quando a marca daquele hostname está indisponível. Ela responde ainda
 *   **antes** do spinner de carregamento, senão uma chamada lenta de identidade
 *   faria a sonda receber HTML de "Carregando…".
 *
 * `/renova_plano` continua atrás do carregamento dos settings (precisa do
 * `mainpage.json` para saber qual layout renderizar), mas não do gate de marca.
 */

const layoutMap: Record<string, React.ComponentType<{ page: PageConfig }>> = {
  default: LayoutDefault,
  legacy: LayoutLegacy,
};

function renderPage(page: PageConfig) {
  const LayoutComponent = layoutMap[page.layout] || LayoutDefault;
  return <LayoutComponent page={page} />;
}

export function AppRoutes() {
  const { settings, loading: settingsLoading, error } = useSettings();
  const { loading: brandLoading, error: brandError, isBlocked } = useBrand();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  /**
   * `/healthcheck` responde **antes** de qualquer espera.
   *
   * É sonda de infraestrutura: se ela ficar atrás do spinner de carregamento, um
   * middleware lento faz a sonda receber o HTML de "Carregando…" em vez de
   * `Teste OK`, e o container é marcado como não saudável por um motivo que não
   * tem nada a ver com ele.
   */
  if (pathname === ROUTES.healthcheck) {
    return (
      <Routes>
        <Route path={ROUTES.healthcheck} element={<Healthcheck />} />
      </Routes>
    );
  }

  if (settingsLoading || brandLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress sx={{ color: 'var(--color-primary, #02EA75)' }} />
        <Typography variant="body2" color="text.secondary">
          {t.layout.loading}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
        px={2}
        textAlign="center"
      >
        <Typography variant="h5" color="error">
          {t.layout.errorTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t.layout.errorBody}
        </Typography>
      </Box>
    );
  }

  /**
   * Duas situações levam à tela de indisponível, as mesmas do
   * `service_unavailable.blade.php`:
   *
   * 1. a marca existe mas está com o Recarga Fácil desligado no ERP
   *    (`st_recarga_facil = 0`);
   * 2. a identidade da marca não pôde ser carregada — hostname ainda não
   *    cadastrado no `ACCESS_KEY` do middleware, marca inexistente (404) ou
   *    API fora do ar. Com um domínio por marca não há fallback possível:
   *    exibir a identidade Contel no domínio da marca seria pior que a tela de
   *    indisponível.
   */
  const brandUnavailable = isBlocked || Boolean(brandError);

  const legacyPages = settings.pages.filter((page) => page.route === ROUTES.legacy);
  const brandPages = settings.pages.filter((page) => page.route !== ROUTES.legacy);

  return (
    <Routes>
      <Route path={ROUTES.healthcheck} element={<Healthcheck />} />

      {legacyPages.map((page) => (
        <Route key={page.route} path={page.route} element={renderPage(page)} />
      ))}

      {brandUnavailable ? (
        <Route path="*" element={<NotFound />} />
      ) : (
        <>
          {brandPages.map((page) => (
            <Route key={page.route} path={page.route} element={renderPage(page)} />
          ))}
          <Route path="*" element={<NotFound />} />
        </>
      )}
    </Routes>
  );
}
